import { memo, useEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useProgressBar } from 'react-aria';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorBrand } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Gap, Padding } from '../theme/Spaces.stylex';
import { locale } from '../../primitives/I18n.gen';
import { Tooltip, TooltipSpan } from '../overlays/Tooltip';
import { Title } from '../typography/Title';
import { TextStyle } from '../typography/TextStyle';
export type Tone = 'neutral' | 'brand';
type Props = {
  value?: number;
  minValue?: number;
  maxValue?: number;
  tone?: Tone;
  label?: string;
  ariaLabel?: string;
  capValue?: boolean;
  pending?: boolean;
};
const MIN_FILL_WIDTH = '1px';
const PENDING_VALUE = '—';
const percentFormatter = new Intl.NumberFormat(locale, {
  style: 'percent',
  maximumFractionDigits: 0
});
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';
const sheen = "xroe53j-B";
const _temp = {
  "width-kzqmXN": "width-x5lhr3w",
  "$$css": "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:91"
};
const styles = {
  root: {
    "display-k1xSpc": "display-x1lliihq",
    "width-kzqmXN": "width-xh8yej3",
    "height-kZKoxP": "height-xdk7pt",
    "padding-kmVPX3": "padding-xdqt6wg",
    "boxSizing-kB7OPa": "boxSizing-x9f619",
    $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:57"
  },
  fill: {
    "position-kVAEAm": "position-x1n2onr6",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "height-kZKoxP": "height-xqu0tyb",
    "maxWidth-ks0D6T": "maxWidth-x193iq5w",
    "transition-kmkexE": "transition-x16qf6z8 transition-x9kvfbb",
    "::after_content-k5JduY": "content-x1s928wv",
    "::after_position-kwXMNM": "position-x1j6awrg",
    "::after_inset-kv0HGH": "inset-xarstr8",
    "::after_backgroundImage-kNctxI": "backgroundImage-x794thw",
    "::after_transform-kLigFv": "transform-xvs0bi2",
    "::after_animationName-kFguJn": "animationName-xxajvwa animationName-x1j82dal",
    "::after_animationDuration-kh3gPk": "animationDuration-x1ja5ae0",
    "::after_animationDelay-kDrcHm": "animationDelay-x6jhwpr",
    "::after_animationTimingFunction-kYqdJW": "animationTimingFunction-x1hl23lj",
    "::after_animationFillMode-kFzpKn": "animationFillMode-x1idobqq",
    $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:64"
  },
  fillFromPercentage: (percentage: number) => [_temp, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`max(${MIN_FILL_WIDTH}, ${percentage}%)`)
  }],
  fill_pending: {
    "width-kzqmXN": "width-xh8yej3",
    "opacity-kSiTet": "opacity-x3onkmb",
    "::after_animationDelay-kDrcHm": "animationDelay-x1fi5hyh",
    "::after_animationDuration-kh3gPk": "animationDuration-x1ui9q4k",
    "::after_animationIterationCount-kVUETb": "animationIterationCount-x13ee8us",
    $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:94"
  }
};
const toneStyles = {
  neutral: {
    root: {
      "backgroundColor-kWkggS": "backgroundColor-x1y0x0bx",
      $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:107"
    },
    fill: {
      "backgroundColor-kWkggS": "backgroundColor-xug7tn3",
      $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:108"
    }
  },
  brand: {
    root: {
      "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
      $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:111"
    },
    fill: {
      "backgroundColor-kWkggS": "backgroundColor-x1ryq1pb",
      $$css: "wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:112"
    }
  }
};
export const ProgressBar = memo(function ProgressBarComponent({
  value,
  minValue = 0,
  maxValue = 100,
  tone = 'neutral',
  label,
  ariaLabel,
  capValue = false,
  pending = false
}: Props) {
  const clampedValue = Math.min(Math.max(value ?? minValue, minValue), maxValue);
  const displayedValue = capValue ? clampedValue : value ?? minValue;
  const {
    progressBarProps
  } = useProgressBar({
    value: displayedValue,
    minValue,
    maxValue,
    isIndeterminate: pending,
    'aria-label': ariaLabel ?? label ?? ''
  });

  // NOTE - mounts empty then grows to its value, so the bar animates on first display
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  const range = maxValue - minValue;
  const percentage = mounted && range > 0 ? (clampedValue - minValue) / range * 100 : 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const bar = <div {...progressBarProps} {...stylex.props(styles.root, toneStyles[tone].root)}>
      <div {...stylex.props(styles.fill, pending ? styles.fill_pending : styles.fillFromPercentage(percentage), toneStyles[tone].fill)} />
    </div>;
  if (label === undefined) return bar;
  return <div ref={containerRef} className="display-x78zum5 flexDirection-xdt5ytf gap-x851vlu width-xh8yej3" data-style-src="wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:45">
      <div className="display-x78zum5 alignItems-x1pha0wt justifyContent-x1qughib gap-x851vlu" data-style-src="wino-gc:src/resources/feedback-indicators/ProgressBar.tsx:51">
        <Title level={5} weight="semibold">
          {label}
        </Title>
        <Tooltip placement="top" arrowed disabled={pending} altTriggerRef={containerRef} delay={300} content={<TooltipSpan text={percentFormatter.format(range > 0 ? (displayedValue - minValue) / range : 0)} />}>
          <TextStyle size="small" variation="neutral" wrap={false}>
            {`${pending ? PENDING_VALUE : displayedValue} / ${maxValue}`}
          </TextStyle>
        </Tooltip>
      </div>
      {bar}
    </div>;
});